import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser, Platform } from '@app/classes';
import { ConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { LeaveOfAbsenceTypes } from '@app/models/employee/leave-of-absence-types.model';
import { AnalyticService, AuthService, PayrollService } from '@app/services';
import { NotifyService } from '@app/services/notify.service';
import { AssignTaskTemplatesService, EmployeeRoeService } from '@employees/services';
import { Employee } from '@models/employee/employee.model';
import { LeaveOfAbsence } from '@models/employee/leave-of-absence.model';
import { TaskTemplate } from '@models/tasks/task-template.model';
import moment from 'moment';

@Component({
    selector: 'app-employee-leave-of-absence',
    templateUrl: './employee-leave-of-absence.template.html',
    styleUrls: ['./employee-leave-of-absence.style.scss'],
})
export class EmployeeLeaveOfAbsence {
    @ViewChild('generateRoeModal') generateRoeModal!: ConfirmationDialogComponent;

    isLoading = false;
    showRetroactiveModal = false;
    showRetroactiveDialog = false;
    showNextStepsModal = false;
    backDatedLeaveOfAbsenceStartDateAffirmed = true;
    leaveOfAbsence = new LeaveOfAbsence();
    employee = new Employee();
    lastRecurringPayrollEndDate: Date | null = null;

    mode: 'create' | 'edit';

    selectedTaskTemplates: TaskTemplate[] = [];
    leaveOfAbsenceTypes: LeaveOfAbsenceTypes[] = [];

    constructor(
        private auth: AuthService,
        private payrollService: PayrollService,
        private route: ActivatedRoute,
        private assignTaskTemplatesService: AssignTaskTemplatesService,
        private router: Router,
        private notify: NotifyService,
        private analyticService: AnalyticService,
        private employeeRoeService: EmployeeRoeService
    ) {
        this.employee = this.route.snapshot.data.models.get('employee');
        this.mode = this.route.snapshot.queryParams.mode || 'create';

        if (this.mode === 'edit') {
            this.leaveOfAbsence = this.employee.leaveOfAbsences[0];
        }
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        this.lastRecurringPayrollEndDate = this.employee.activeOnPayroll()
            ? await this.payrollService.getLastRecurringPayrollEndDate(this.auth.company.id)
            : null;
        try {
            const [leaveOfAbsenceTypes] = await LeaveOfAbsenceTypes.all();

            this.leaveOfAbsenceTypes = leaveOfAbsenceTypes;
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    async onSave(): Promise<void> {
        this.isLoading = true;

        try {
            await this.leaveOfAbsence.param('employeeId', this.employee.id).save();

            if (this.selectedTaskTemplates.length) {
                await this.assignTasks();
            }

            this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileMeatballPlaceOnLeaveClickCompleteToSave);

            this.notify.success({
                key: 'employees.leaveOfAbsence.storeSuccess',
                params: {
                    employeeName: this.employee.firstName || '',
                },
            });

            let isNavigating = false;

            if (this.shouldGenerateRoe()) {
                isNavigating = await this.promptRoeDecision();
            }

            // If the user opted to create the ROE now, they are currently
            // navigating to the ROE page and we shouldn't interrupt that.
            if (!isNavigating) {
                this.router.navigate(['/', 'employees', 'directory', this.employee.id]);
            }
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onConfirm(): void {
        if (this.employee.activeOnPayroll()) {
            this.showNextStepsModal = true;
            return;
        }

        this.onSave();
    }

    leaveOfAbsenceStartDateChange(changed: boolean): void {
        this.backDatedLeaveOfAbsenceStartDateAffirmed = !changed;
    }

    shouldMoveToNextStep = async (): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            if (
                this.leaveOfAbsence.isDirty() &&
                !this.backDatedLeaveOfAbsenceStartDateAffirmed &&
                this.startingBeforeLastPaidPayroll()
            ) {
                this.showRetroactiveModal = true;
                this.showRetroactiveDialog = true;
                reject();
            }

            resolve();
        });
    };

    private startingBeforeLastPaidPayroll(): boolean {
        if (!this.employee.activeOnPayroll()) {
            return false;
        }

        if (this.lastRecurringPayrollEndDate === null) {
            return false;
        }

        return moment(this.leaveOfAbsence.start).isBefore(this.lastRecurringPayrollEndDate);
    }

    private async assignTasks(): Promise<void> {
        await this.assignTaskTemplatesService.onSave(this.selectedTaskTemplates, [this.employee]);
    }

    private shouldGenerateRoe(): boolean {
        return (
            this.employee.activeOnPayroll() && this.auth.can(Platform.payroll.permission.view) && this.mode === 'create'
        );
    }

    /**
     * Prompt the user to decide if they want to create the ROE now, or set a reminder to do it later.
     *
     * Returns a promise resolving to a boolean, indicating whether navigation
     * is taking place as a result of this decision.
     */
    private async promptRoeDecision(): Promise<boolean> {
        const willGenerateRoe = await this.generateRoeModal.open();

        try {
            if (willGenerateRoe) {
                return await this.employeeRoeService.issueRoe(this.employee);
            } else {
                await this.employeeRoeService.generateReminder(this.employee, this.leaveOfAbsence.start);
            }
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }

        return false;
    }
}
